//MonthlyPlanSection.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import styles from "./MonthlyPlanSection.module.css";
import templatePt from "../../data/pt/monthly_plan_templates.json";
import templateEn from "../../data/en/monthly_plan_templates.json";

function normalizeKey(input = "") {
  if (!input) return "default";
  const noDiacritics = input.normalize
    ? input.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    : input;
  return String(noDiacritics).toLowerCase().trim().replace(/\s+/g, "-");
}

// Picks the lowest trait value; if there's a tie, resolves by the priority order:
// emotionalOpenness > discipline > mentalOpenness > perspective
function pickLowestTrait(
  characterTraits = {
    discipline: 50,
    emotionalOpenness: 50,
    mentalOpenness: 50,
    perspective: 50,
  }
) {
  // Ensure keys exist and are numbers
  const keys = [
    "emotionalOpenness",
    "discipline",
    "mentalOpenness",
    "perspective",
  ];
  const values = keys.map((k) => ({
    key: k,
    value: Number(characterTraits[k] ?? 0),
  }));
  // Find minimum value
  const minVal = Math.min(...values.map((v) => v.value));
  // Get all keys that have the min value
  const tied = values.filter((v) => v.value === minVal).map((v) => v.key);
  if (tied.length === 1) return tied[0];
  // Resolve tie by priority order defined above
  for (let k of keys) {
    if (tied.includes(k)) return k;
  }
  // Fallback: return first key
  return tied[0] || "discipline";
}

export default function MonthlyPlanSection({
  premiumPayload = null,
  userData = null,
  wrapPremium = null,
}) {
  const { i18n, t } = useTranslation("user");
  const template = i18n.language?.startsWith("pt") ? templatePt : templateEn;
  const archeKeyRaw =
    (premiumPayload &&
      (premiumPayload.archetypeKey || premiumPayload.archetype)) ||
    "";
  const archeKey = normalizeKey(archeKeyRaw);
  const tpl = template[archeKey] || template["default"] || null;

  const characterTraits = (premiumPayload &&
    premiumPayload.characterTraits) || {
    discipline: 50,
    emotionalOpenness: 50,
    mentalOpenness: 50,
    perspective: 50,
  };
  const lowestTrait = useMemo(
    () => pickLowestTrait(characterTraits),
    [characterTraits]
  );

  if (!tpl)
    return (
      <div className={styles.missing}>{t("profile.monthlyPlanMissing")}</div>
    );

  // deep copy base
  const base = JSON.parse(JSON.stringify(tpl.monthlyPlan));
  const adjustments = base.traitAdjustments || {};

  const adj = adjustments[lowestTrait] || null;
  if (adj && adj.week_overrides) {
    for (let i = 0; i < base.weeks.length; i++) {
      const wk = base.weeks[i];
      const ov = adj.week_overrides[String(wk.week)];
      if (ov) {
        if (ov.activities)
          wk.activities = (wk.activities || []).concat(ov.activities);
        if (ov.daily_micro_habit) wk.daily_micro_habit = ov.daily_micro_habit;
      }
    }
  }

  return (
    <section className={styles.monthlyPlan}>
      <header className={styles.header}>
        <h3>{t("profile.monthlyPlanTitle")}</h3>
      </header>

      <div className={styles.intro}>
        <p>{base.overview}</p>
        <p>
          <strong>{t("profile.monthlyPlanHowToUse")}</strong> {base.howToUse}
        </p>
        {adj && adj.note && (
          <p className={styles.traitNote}>
            <strong>{t("profile.monthlyPlanHowToUse")}</strong> {adj.note}
          </p>
        )}
      </div>
      {wrapPremium(
        <div className={styles.weeks}>
          {base.weeks.map((wk) => (
            <article key={wk.week} className={styles.weekCard}>
              <h4>
                {t("profile.monthlyPlanWeek")}
                {wk.week} — {wk.title}
              </h4>
              <p className={styles.objective}>
                <strong>{t("profile.monthlyPlanObjective")}</strong>{" "}
                {wk.objective}
              </p>
              <p>
                <strong>{t("profile.monthlyPlanObjective")}</strong>{" "}
                {wk.daily_micro_habit}
              </p>
              <p>
                <strong>{t("profile.monthlyPlanObjective")}</strong>{" "}
                {wk.act_focus}
              </p>
              <ul className={styles.activities}>
                {wk.activities.map((a, i) => (
                  <li key={i}>{a}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        <small>{t("profile.monthlyPlanFooter")}</small>
      </div>
    </section>
  );
}
